// TODO: После доработки UI компонентов Input, Checkbox и других - заменить MUI компоненты на наши кастомные
// и применить их в этой форме авторизации

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useAppContext } from "../../../../AppContext";
import PasswordTextField from "../../../../components/common/PasswordTextField";
import LinkWithRedirect from "../../../../components/common/LinkWithRedirect";
import { useErrorBoundary } from "react-error-boundary";
import { Button } from "../../../../ui/Button/Button";
import { LoginSchema } from "./schemas/LoginSchema";
import { ROUTES } from "../../../../constants/routes";
import classes from "./styles.module.scss";

export interface LoginFormProps {}

type AutofillState = { email: boolean; pwd: boolean };

export const LoginForm = (props: LoginFormProps) => {
  const { api } = useAppContext();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { showBoundary } = useErrorBoundary();
  const [isAutoFilled, setIsAutoFilled] = useState<AutofillState>({
    email: false,
    pwd: false,
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      pwd: "",
      rememberMe: false,
    },
    validationSchema: LoginSchema(t),
    onSubmit: async (values) => {
      void api.authApi
        .auth({
          login: values.email,
          password: values.pwd,
          rememberMe: values.rememberMe,
        })
        .then((result) => {
          if (result.success) {
            if (params.has("redirect")) {
              const redirectUrl = params.get("redirect");
              navigate(redirectUrl || ROUTES.DASHBOARD);
            } else {
              navigate(ROUTES.DASHBOARD);
            }
          } else {
            setErrorMessage(
              result.errors?.[0] || "Ошибка при получении ответа от сервера"
            );
          }
        })
        .catch((e) => {
          if (
            e?.response?.data?.errors &&
            Object.keys(e.response.data?.errors)?.length
          ) {
            setErrorMessage(Object.values(e.response.data.errors).join("\n"));
            return;
          }

          if (e.response.status === 400) {
            setErrorMessage(
              e.response?.data?.title || e.response?.data || e.message
            );
            return;
          }

          showBoundary(e);
        });
    },
  });

  useEffect(() => {
    setTimeout(() => {
      document.querySelectorAll("input:-webkit-autofill").forEach((input) => {
        if (input.getAttribute("name") === "email") {
          setIsAutoFilled((prev) => ({ ...prev, email: true }));
        } else if (input.getAttribute("name") === "pwd") {
          setIsAutoFilled((prev) => ({ ...prev, pwd: true }));
        }
      });
    }, 1000);
  }, []);

  useEffect(() => {
    setIsAutoFilled((prev) => ({
      ...prev,
      email: !!formik.values.email,
      pwd: !!formik.values.pwd,
    }));
  }, [formik.values.email, formik.values.pwd]);

  const navigate = useNavigate();

  return (
    <div className={classes.wrapper}>
      <Typography variant="h4" className={classes.title}>
        {t("login")}
      </Typography>

      <form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
        <Stack spacing={56}>
          <Grid container gap={37} style={{ margin: "46px 0" }}>
            <Grid item xs={12} style={{ position: "relative" }}>
              <TextField
                required
                name="email"
                className="yna"
                autoComplete="off"
                label={t("emailInput")}
                variant="outlined"
                value={formik.values.email}
                fullWidth
                onChange={(e) => {
                  formik.handleChange(e);
                  if (errorMessage) setErrorMessage(undefined);
                }}
                onBlur={formik.handleBlur}
                error={
                  (formik.errors.email !== undefined &&
                    formik.submitCount > 0) ||
                  errorMessage !== undefined
                }
                InputLabelProps={{ shrink: isAutoFilled.email }}
              />
              {formik.errors.email && formik.submitCount > 0 ? (
                <div className={classes.errorMessage}>
                  {formik.errors.email}
                </div>
              ) : null}
              {errorMessage && !formik.errors.pwd ? (
                <div className={classes.errorMessage}>{errorMessage}</div>
              ) : null}
            </Grid>
            <Stack spacing={19} style={{ width: "100%" }}>
              <Grid item xs={12} style={{ position: "relative" }}>
                <PasswordTextField
                  required
                  name="pwd"
                  className="yna"
                  autoComplete="off"
                  label={t("passwordInput")}
                  variant="outlined"
                  fullWidth
                  error={
                    (formik.errors.pwd !== undefined &&
                      formik.submitCount > 0) ||
                    errorMessage !== undefined
                  }
                  value={formik.values.pwd}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.handleChange(e);
                    if (errorMessage) setErrorMessage(undefined);
                  }}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: isAutoFilled.pwd }}
                />
                {formik.errors.pwd && formik.submitCount > 0 ? (
                  <div className={classes.errorMessage}>
                    {formik.errors.pwd}
                  </div>
                ) : null}
                {errorMessage && !formik.errors.pwd ? (
                  <div className={classes.errorMessage}>{errorMessage}</div>
                ) : null}
              </Grid>

              <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
                className={classes.rememberWrapper}
              >
                <Grid item>
                  <FormControlLabel
                    name="rememberMe"
                    label={t("rememberMe")}
                    control={<Checkbox size="small" />}
                    checked={formik.values.rememberMe}
                    value={formik.values.rememberMe}
                    onChange={formik.handleChange}
                    className={classes.checkboxLabel}
                  />
                </Grid>
                <Grid item>
                  <Link
                    to={ROUTES.LOGIN + ROUTES.RESET_PASSWORD}
                    className={classes.forgotPasswordLink}
                  >
                    <Typography component="span">
                      {t("forgotPassword")}
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          <Stack spacing={17}>
            <Button type="submit" fullWidth buttonSize="lg">
              {t("login")}
            </Button>

            <Grid
              container
              flexWrap={"wrap"}
              justifyContent={"center"}
              className={classes.registrationWrapper}
            >
              <Grid item display={"flex"} alignContent={"center"}>
                <Typography
                  className={classes.registrationPrompt}
                  variant="body1"
                >
                  {t("notRegistered")}
                </Typography>
              </Grid>
              <Grid
                item
                flexWrap={"wrap"}
                display={"flex"}
                alignContent={"center"}
              >
                <LinkWithRedirect
                  to={ROUTES.LOGIN + ROUTES.REGISTER}
                  className={classes.loginPageLink}
                >
                  <Typography component="span">
                    &nbsp;{t("notRegisteredCreateAccount")}
                  </Typography>
                </LinkWithRedirect>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </form>
    </div>
  );
};
